import { NetworkType } from 'app/state/network/types'

export const TRANSACTIONS_LIMIT = 20

export const consensusDecimals = 6

// Moved outside backend.ts to avoid circular dependency
export enum BackendAPIs {
  OasisScanV2 = 'oasisscanV2',
  Nexus = 'nexus',
}

type BackendApiUrls = {
  explorer: string
  blockExplorer: string
  blockExplorerParatimes?: string
  blockExplorerAccount?: string
}

type BackendProviders = {
  grpc: string
  ticker: string // from nic.stakingTokenSymbol()
  min_delegation: number // from nic.stakingConsensusParameters().min_delegation
  [BackendAPIs.OasisScanV2]: BackendApiUrls
  [BackendAPIs.Nexus]: BackendApiUrls
}

type BackendConfig = {
  [key in NetworkType]: BackendProviders
}

export const config: BackendConfig = {
  // Mainnet is disabled in the UI; placeholders are left here only to satisfy the type.
  mainnet: {
    grpc: 'about:blank',
    ticker: 'BLUAI',
    min_delegation: 100,
    [BackendAPIs.OasisScanV2]: {
      explorer: 'about:blank',
      blockExplorer: 'about:blank',
      blockExplorerParatimes: 'about:blank',
      blockExplorerAccount: 'about:blank',
    },
    [BackendAPIs.Nexus]: {
      explorer: 'about:blank',
      blockExplorer: 'about:blank',
      blockExplorerParatimes: 'about:blank',
      blockExplorerAccount: 'about:blank',
    },
  },
  testnet: {
    grpc: 'http://20.243.200.221:8549/grpc',
    ticker: 'TBLUAI',
    min_delegation: 100,
    [BackendAPIs.OasisScanV2]: {
      explorer: 'http://20.243.200.221:8549/v1',
      blockExplorer: 'http://20.243.200.221:8548/localnet/consensus/tx/{{txHash}}',
      blockExplorerParatimes:
        'http://20.243.200.221:8548/localnet/{{runtimeId}}/tx/{{txHash}}',
      blockExplorerAccount: 'http://20.243.200.221:8548/localnet/consensus/address/{{address}}',
    },
    [BackendAPIs.Nexus]: {
      explorer: 'http://20.243.200.221:8549/v1',
      blockExplorer: 'http://20.243.200.221:8548/localnet/consensus/tx/{{txHash}}',
      blockExplorerParatimes:
        'http://20.243.200.221:8548/localnet/{{runtimeId}}/tx/{{txHash}}',
      blockExplorerAccount: 'http://20.243.200.221:8548/localnet/consensus/address/{{address}}',
    },
  },
  local: {
    grpc: 'http://localhost:42280',
    ticker: 'TEST',
    min_delegation: 100,
    [BackendAPIs.OasisScanV2]: {
      explorer: 'http://20.243.129.119:8547/v1',
      blockExplorer: 'http://20.243.129.119:8548/localnet/consensus/tx/{{txHash}}',
      blockExplorerParatimes:
        'http://20.243.129.119:8548/localnet/{{runtimeId}}/tx/{{txHash}}',
      blockExplorerAccount: 'http://20.243.129.119:8548/localnet/consensus/address/{{address}}',
    },
    [BackendAPIs.Nexus]: {
      explorer: 'http://20.243.129.119:8547/v1',
      blockExplorer: 'http://20.243.129.119:8548/localnet/consensus/tx/{{txHash}}',
      blockExplorerParatimes:
        'http://20.243.129.119:8548/localnet/{{runtimeId}}/tx/{{txHash}}',
      blockExplorerAccount: 'http://20.243.129.119:8548/localnet/consensus/address/{{address}}',
    },
  },
}

type ParaTimeNetwork = {
  address: string
  runtimeId: string
}

export type ParaTimeConfig = {
  mainnet: ParaTimeNetwork
  testnet: ParaTimeNetwork
  local: Partial<ParaTimeNetwork>
  gasPrice: bigint
  feeGas: bigint
  decimals: number
  displayOrder: number
  type: RuntimeTypes
}

export enum RuntimeTypes {
  Evm = 'evm',
  Oasis = 'oasis',
}

const emeraldConfig: ParaTimeConfig = {
  mainnet: {
    address: 'oasis1qzvlg0grjxwgjj58tx2xvmv26era6t2csqn22pte',
    runtimeId: '000000000000000000000000000000000000000000000000e2eaa99fc008f87f',
  },
  testnet: {
    address: 'oasis1qr629x0tg9gm5fyhedgs9lw5eh3d8ycdnsxf0run',
    runtimeId: '00000000000000000000000000000000000000000000000072c8215e60d5bca7',
  },
  local: {
    address: undefined,
    runtimeId: undefined,
  },
  gasPrice: 100n,
  feeGas: 70_000n,
  decimals: 18,
  displayOrder: 2,
  type: RuntimeTypes.Evm,
}

const cipherConfig: ParaTimeConfig = {
  mainnet: {
    address: 'oasis1qrnu9yhwzap7rqh6tdcdcpz0zf86hwhycchkhvt8',
    runtimeId: '000000000000000000000000000000000000000000000000e199119c992377cb',
  },
  testnet: {
    address: 'oasis1qqdn25n5a2jtet2s5amc7gmchsqqgs4j0qcg5k0t',
    runtimeId: '0000000000000000000000000000000000000000000000000000000000000000',
  },
  local: {
    address: undefined,
    runtimeId: undefined,
  },
  gasPrice: 5n,
  feeGas: 5_000_000n,
  decimals: 9,
  displayOrder: 3,
  type: RuntimeTypes.Oasis,
}

const sapphireConfig: ParaTimeConfig = {
  mainnet: {
    address: 'oasis1qrd3mnzhhgst26hsp96uf45yhq6zlax0cuzdgcfc',
    runtimeId: '000000000000000000000000000000000000000000000000f80306c9858e7279',
  },
  testnet: {
    // bluwhale sapphire-localnet runtime ID (standard sapphire-localnet ID).
    // Paratime consensus-deposit beneficiary address is the runtime-derived
    // consensus address (oasis-core NewRuntimeAddress(runtimeId), bech32 with
    // OXN HRP). With the same-origin /grpc/ envoy bridge in the container,
    // consensus gRPC IS reachable, so deposit flow does run.
    address: 'oxn1qpk3ddug998te72alapzncuzge0tq5c3gyac8798',
    runtimeId: '0000000000000000000000000000000000000000000000005d618ea626d08201',
  },
  local: {
    address: undefined,
    runtimeId: undefined,
  },
  gasPrice: 100n,
  // ConsensusDeposit on sapphire-localnet needs ~117k gas; 70_000n was the
  // upstream EVM transfer default and trips "out of gas" for deposits.
  // 500_000n leaves ~4x headroom (matches the saga test fixture value).
  feeGas: 500_000n,
  decimals: 18,
  displayOrder: 1,
  type: RuntimeTypes.Evm,
}

export enum ParaTime {
  Cipher = 'cipher',
  Emerald = 'emerald',
  Sapphire = 'sapphire',
}

type ParaTimesConfig = {
  [key in ParaTime]: ParaTimeConfig
}

export const paraTimesConfig: ParaTimesConfig = {
  [ParaTime.Cipher]: cipherConfig,
  [ParaTime.Emerald]: emeraldConfig,
  [ParaTime.Sapphire]: sapphireConfig,
}

const splitUrls = (input: string | undefined): string[] =>
  input
    ? input
        .split(',')
        .filter(s => !!s)
        .map(s => s.trim())
    : []

export const deploys = {
  production: splitUrls(process.env.REACT_APP_PRODUCTION_URLS),
  staging: splitUrls(process.env.REACT_APP_STAGING_URLS),
  extension: 'chrome-extension://ppdadbejkmjnefldpcdjhnkpbjkikoip',
  localhost: 'http://localhost:3000',
}
